import { useCallback, useMemo } from 'react'
import { Row, Col, Select, Popover, Dropdown } from 'antd'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import { ActionCreators } from 'redux-undo'
import Barcode from '../../../Components/Kiosk/Barcode'
import TagFilters from '../../../Components/Table/TableComponents/TagFilters'
import ClearFilters from '../../../Components/Table/TableComponents/ClearFilters'
import MenuIcon from '../../../Components/Svg/MenuIcon'
import StoreIcon from '../../../Components/Svg/StoreIcon'
import ItalyIcon from '../../../Components/Svg/ItalyIcon'
import WorldIcon from '../../../Components/Svg/WorldIcon'
import EditIcon from '../../../Components/Svg/EditIcon'
import { setDrawerVisibility, toggleEditTable, setFilterSearchValue } from '../../../Slices/Kiosk/TableOptionsSlice'
import { populateTableWithArticles, fetchAllArticleDetails } from '../../../Services/Kiosk/ArticleServices'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { renderTranslation } from '../../../Helpers/GenericHelpers'
import {
  AllOption,
  AvailabilitiesOption,
  ExistanceOption,
  MovementsOption,
  ConfigurationForm,
  Stores,
  ProfilesKeys,
} from '../../../Constants/Configuration'
import {
  setActiveStore,
  setShowFilterElement,
  setShowEditButtonElement,
  setShowDimensionLocalElement,
  setShowDimensionNearbyElement,
  setShowDimensionRemoteElement,
  setShowInterrogationTypeElement,
} from '../../../Slices/ConfigurationSlice'
import { saveRowKeys } from '../../../Slices/Kiosk/ArticleSlice'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import TagContainer from 'Components/Kiosk/TagContainer'

const { Option } = Select

export type UpperMenuProps = {
  isGridUpperMenu: boolean
}

function UpperMenu(props: UpperMenuProps) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const editTableDisable = useAppSelector(state => state.present.tableOptions.editTableDisable)
  const filterSearchValue = useAppSelector(state => state.present.tableOptions.filterSearchCurrentValue)
  const activeStore = useAppSelector(state => state.present.configuration.activeStore)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const isEditVisible = useAppSelector(state => state.present.configuration.isEditVisible)
  const workspaceDiwaEditVisible = useAppSelector(state => state.present.configuration.workspaceDiwaEditVisible)
  const formConfigurationValue = useAppSelector(state => state.present.configuration.formConfigurationValue)
  const profileValue = useAppSelector(state => state.present.configuration.profileValue)

  const filterElementVisible = useAppSelector(state => state.present.configuration.workspaceFilterElementVisible)
  const dimensionLocalElementVisible = useAppSelector(
    state => state.present.configuration.workspaceDimensionLocalVisible,
  )
  const dimensionNearbyElementVisible = useAppSelector(
    state => state.present.configuration.workspaceDimensionNearbyVisible,
  )
  const dimensionRemoteElementVisible = useAppSelector(
    state => state.present.configuration.workspaceDimensionRemoteVisible,
  )
  const interrogationTypeElementVisible = useAppSelector(
    state => state.present.configuration.workspaceInterrogationTypeElementVisible,
  )
  const movementTabVisible = useAppSelector(state => state.present.configuration.isTabMovementVisible)
  const workspaceMovementVisible = useAppSelector(state => state.present.configuration.workspaceMovementVisible)

  const past = useAppSelector(state => state.past)
  const future = useAppSelector(state => state.future)

  const isConfigurationFormGruccia = formConfigurationValue === ConfigurationForm.GRUCCIA

  const handleSelectAll = useCallback(() => {
    dispatch(toggleEditTable())
  }, [dispatch])

  const handleChangeFilterSearchValue = useCallback(
    value => {
      dispatch(setFilterSearchValue({ dropdownFilterValue: value, isEditDisable: editTableDisable }))

      dispatch(populateTableWithArticles())
    },
    [dispatch, editTableDisable],
  )

  const changeStore = useCallback(
    storeValue => {
      if (!editTableDisable) {
        return
      }

      dispatch(setActiveStore(storeValue))
      //when change store, can change article availability
      dispatch(populateTableWithArticles())
      if (rowKeysSelected.length === 1 && editTableDisable) {
        fetchAllArticleDetails(
          rowKeysSelected[0],
          formConfigurationValue,
          movementTabVisible && workspaceMovementVisible,
          dispatch,
        )
      } else {
        dispatch(
          saveRowKeys({
            rowKeys: [],
            formValue: formConfigurationValue,
            movementVisible: movementTabVisible && workspaceMovementVisible,
          }),
        )
      }
    },
    [editTableDisable, dispatch, rowKeysSelected, formConfigurationValue, movementTabVisible, workspaceMovementVisible],
  )

  const showFilterDrawer = useCallback(() => {
    if (!editTableDisable) {
      return
    }

    dispatch(setDrawerVisibility(true))
  }, [dispatch, editTableDisable])

  const handleBackwardNavigation = useCallback(() => {
    if (!editTableDisable) {
      return
    }

    dispatch(ActionCreators.undo())
  }, [dispatch, editTableDisable])

  const handleForwardNavigation = useCallback(() => {
    if (!editTableDisable) {
      return
    }

    dispatch(ActionCreators.redo())
  }, [dispatch, editTableDisable])

  const hideSectionMenu = useCallback(
    setVisibility => {
      return {
        items: [
          {
            key: 'disable',
            label: 'Disabilita',
            onClick: () => {
              dispatch(setVisibility(false))
            },
          },
        ],
      }
    },
    [dispatch],
  )

  const stateNavigationBackClass = useMemo(() => {
    return (
      (past.length ? 'state-navigation previous-state-icon active' : 'state-navigation previous-state-icon') +
      (!editTableDisable ? ' disable' : '')
    )
  }, [past, editTableDisable])
  const stateNavigationForwardClass = useMemo(() => {
    return (
      (future.length ? 'state-navigation next-state-icon active' : 'state-navigation next-state-icon') +
      (!editTableDisable ? ' disable' : '')
    )
  }, [future, editTableDisable])

  const gridUpperMenu = (
    <>
      {dimensionLocalElementVisible && (
        <Dropdown
          menu={hideSectionMenu(setShowDimensionLocalElement)}
          trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
        >
          <Popover content={renderTranslation(language, TranslationKeys.storeTips)} trigger='hover'>
            <span className='icon-block-grid'>
              <StoreIcon
                className={
                  'generic-icon store-icon ' +
                  (activeStore === Stores.Local ? ' active' : '') +
                  (!editTableDisable ? ' disable' : '')
                }
                onClick={() => {
                  changeStore(Stores.Local)
                }}
              />
            </span>
          </Popover>
        </Dropdown>
      )}
      {dimensionNearbyElementVisible && (
        <Dropdown
          menu={hideSectionMenu(setShowDimensionNearbyElement)}
          trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
        >
          <Popover content={renderTranslation(language, TranslationKeys.nearStoreTips)} trigger='hover'>
            <span className='icon-block-grid'>
              <ItalyIcon
                className={
                  'generic-icon store-icon ' +
                  (activeStore === Stores.Nearby ? ' active' : '') +
                  (!editTableDisable ? ' disable' : '')
                }
                onClick={() => {
                  changeStore(Stores.Nearby)
                }}
              />
            </span>
          </Popover>
        </Dropdown>
      )}
      {dimensionRemoteElementVisible && (
        <Dropdown
          menu={hideSectionMenu(setShowDimensionRemoteElement)}
          trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
        >
          <Popover content={renderTranslation(language, TranslationKeys.allStoreTips)} trigger='hover'>
            <span className='icon-block-grid'>
              <WorldIcon
                className={
                  'generic-icon store-icon ' +
                  (activeStore === Stores.Remote ? ' active' : '') +
                  (!editTableDisable ? ' disable' : '')
                }
                onClick={() => {
                  changeStore(Stores.Remote)
                }}
              />
            </span>
          </Popover>
        </Dropdown>
      )}
    </>
  )

  const upperMenu = (
    <Row className='upper-menu-row pb-2 pl-2'>
      {!isConfigurationFormGruccia ? (
        <Col
          lg={{ span: 'auto', order: 0 }}
          xl={{ span: 'auto' }}
          xxl={{ span: 'auto' }}
          className='left-table-icon-container left-table-col'
        >
          {filterElementVisible ? (
            <Popover content={renderTranslation(language, TranslationKeys.filterSettingsTips)} trigger='hover'>
              <Dropdown
                menu={hideSectionMenu(setShowFilterElement)}
                trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
              >
                <span className='icon-block no-pad'>
                  <MenuIcon
                    className={'generic-icon menu-icon' + (!editTableDisable ? ' disable' : '')}
                    onClick={() => {
                      showFilterDrawer()
                    }}
                  />
                </span>
              </Dropdown>
            </Popover>
          ) : null}
          <Barcode />
        </Col>
      ) : null}
      <Col
        lg={{ span: 24, order: 2 }}
        xl={{ span: 'auto', order: 1 }}
        xxl={{ span: 'auto' }}
        className='tag-filter-col'
      >
        <ClearFilters isFromDiWa={true} />
        <TagContainer />
      </Col>
      {!isConfigurationFormGruccia && (
        <Col
          lg={{ span: 'auto', order: 1 }}
          xl={{ span: 'auto', order: 2 }}
          xxl={{ span: 'auto' }}
          className='right-table-col'
        >
          <div className='utility-container'>
            {isEditVisible && workspaceDiwaEditVisible ? (
              <Popover content={renderTranslation(language, TranslationKeys.enableEditTips)} trigger='hover'>
                <Dropdown
                  menu={hideSectionMenu(setShowEditButtonElement)}
                  trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
                >
                  <span>
                    <EditIcon
                      className={editTableDisable ? 'generic-icon edit-icon' : 'generic-icon edit-icon active'}
                      fillFirst={editTableDisable ? 'currentColor' : 'var(--edit-table-icon-active)'}
                      fillSecond={editTableDisable ? 'currentColor' : 'var(--disable)'}
                      onClick={e => {
                        handleSelectAll()
                      }}
                    />
                  </span>
                </Dropdown>
              </Popover>
            ) : null}
            <span>
              <Popover content={renderTranslation(language, TranslationKeys.stepBack)} trigger='hover'>
                <CaretLeftOutlined
                  className={stateNavigationBackClass}
                  onClick={() => {
                    handleBackwardNavigation()
                  }}
                />
              </Popover>
            </span>
            <span className='vertical-divider'></span>
            <span>
              <Popover content={renderTranslation(language, TranslationKeys.stepForward)} trigger='hover'>
                <CaretRightOutlined
                  className={stateNavigationForwardClass}
                  onClick={() => {
                    handleForwardNavigation()
                  }}
                />
              </Popover>
            </span>

            {interrogationTypeElementVisible ? (
              <Dropdown
                menu={hideSectionMenu(setShowInterrogationTypeElement)}
                trigger={profileValue === ProfilesKeys.SuperProfile ? ['contextMenu'] : []}
                className='ml-3'
              >
                <Select
                  value={filterSearchValue}
                  onChange={handleChangeFilterSearchValue}
                  className='upper-section-dropdown'
                  disabled={!editTableDisable}
                >
                  <Option value={MovementsOption}>
                    {renderTranslation(language, TranslationKeys.searchFilterDropdownMovements)}
                  </Option>
                  <Option value={AvailabilitiesOption}>
                    {renderTranslation(language, TranslationKeys.searchFilterDropdownAvailabilities)}
                  </Option>
                  <Option value={ExistanceOption}>
                    {renderTranslation(language, TranslationKeys.searchFilterDropdownExistance)}
                  </Option>
                  <Option value={AllOption}>
                    {renderTranslation(language, TranslationKeys.searchFilterDropdownAll)}
                  </Option>
                </Select>
              </Dropdown>
            ) : null}
          </div>
        </Col>
      )}
    </Row>
  )

  return props.isGridUpperMenu ? gridUpperMenu : upperMenu
}

export default UpperMenu
